import { earnings_filter_param } from './earnings_logic';
import {
  enum_param,
  first_param,
  merge_search,
  parse_sort,
  sort_token,
  uuid_param
} from '../shared/url_query';

/*
 * pure request builders for the Overview endpoints. Every enum here mirrors a goserver whitelist
 * (overview_plan.go); params.test.js pins them against the backend-generated overview_enums.json.
 */

// row_state: overview_plan.go l1RowStateFilters. The UI only exposes the needs-review filter.
export const ROW_STATE_NEEDS_REVIEW = 'needs_review';

// kind: overview_plan.go drilldownKind*.
export const DRILLDOWN_KIND = { ITEMS: 'items', PENALTIES: 'penalties', ALL: 'all' };

// The ADDITIONAL category answers with additional-payment entries: response and rows carry this kind.
export const DRILLDOWN_CATEGORY_ADDITIONAL = 'ADDITIONAL';
export const DRILLDOWN_KIND_ADDITIONAL = 'additional';

// overview_plan.go catUnattributed: penalties not linked to a session type (manual, month-level, legacy NULL category).
export const DRILLDOWN_CATEGORY_UNATTRIBUTED = 'UNATTRIBUTED';

// overview_plan.go sessionStatusUnknown: session rows whose status was never recorded (legacy NULL).
export const SESSION_STATUS_UNKNOWN = 'UNKNOWN';

// Mirrors validateDrilldownParams: an upper-snake status of at most 48 chars; anything else is not sent.
const SESSION_STATUS_RE = /^[A-Z_]{1,48}$/;

// teacher_status: overview_plan.go teacherStatusValues ('' = all, never sent).
export const TEACHER_STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved' },
  { value: 'TEMPORARILY_UNAVAILABLE', label: 'Temporarily unavailable' },
  { value: 'UNDER_PROBATION', label: 'Under probation' },
  { value: 'VISHESH_TEACHER', label: 'Vishesh teacher' },
  { value: 'RESIGNED', label: 'Resigned' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

const TEACHER_STATUS_VALUES = TEACHER_STATUS_OPTIONS.map((o) => o.value);

// sort: overview_plan.go l1SortColumns (whitelisted server-side); dir: overview_plan.go parseSortDir.
export const L1_SORT_KEYS = [
  'teacher_name',
  'teacher_slug',
  'net',
  'gross',
  'one_on_one',
  'group_class',
  'trial',
  'incentives',
  'additional',
  'penalties',
  'waivers',
  'free_pass_balance',
  'lifecycle',
  'base_rate_snapshot'
];
export const SORT_DIRS = ['asc', 'desc'];
export const DEFAULT_L1_SORT = 'teacher_name';
export const DEFAULT_L1_DIR = 'asc';

// Drops empty/nullish values so the backend applies its "all" default.
export const build_query_string = (params = {}) => {
  const qs = new URLSearchParams();
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v === null || v === undefined || v === '') return;
    qs.append(k, String(v));
  });
  return qs.toString();
};

// Shared /CSV filter set; unknown UI values are dropped rather than sent (the backend would 400).
const overview_filters = ({
  pay_month,
  search,
  needs_review_only,
  teacher_status,
  earnings_visible
}) => ({
  pay_month,
  search: (search || '').trim(),
  row_state: needs_review_only ? ROW_STATE_NEEDS_REVIEW : '',
  teacher_status: TEACHER_STATUS_VALUES.indexOf(teacher_status) === -1 ? '' : teacher_status,
  // '1' | '0' (overview_plan.go withEarningsFilter); = every teacher, dropped from the query
  earnings_visible: earnings_filter_param(earnings_visible)
});

const sort_filters = (sort) => ({
  sort: L1_SORT_KEYS.indexOf(sort && sort.key) === -1 ? DEFAULT_L1_SORT : sort.key,
  dir: SORT_DIRS.indexOf(sort && sort.dir) === -1 ? DEFAULT_L1_DIR : sort.dir
});

export const l1_params = (filters, limit, offset) => ({
  ...overview_filters(filters),
  ...sort_filters(filters.sort),
  limit,
  offset
});

export const csv_params = (filters) => ({
  ...overview_filters(filters),
  ...sort_filters(filters.sort)
});

export const drilldown_params = ({ teacher_uuid, pay_month, category, kind, session_status }) => ({
  teacher_uuid,
  pay_month,
  category,
  kind: Object.values(DRILLDOWN_KIND).indexOf(kind) === -1 ? DRILLDOWN_KIND.ALL : kind,
  session_status: SESSION_STATUS_RE.test(session_status || '') ? session_status : ''
});

// ---- deep link (the daily Slack digest: /teacher-payments/overview?pay_month=YYYY-MM&row_state=needs_review) ----

const PAY_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// Pure: reads only the two whitelisted params; anything malformed is dropped so the page falls back to its defaults.
export const parse_overview_deep_link = (search) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const pay_month = (qs.get('pay_month') || '').trim();
  const row_state = (qs.get('row_state') || '').trim().toLowerCase();
  return {
    pay_month: PAY_MONTH_RE.test(pay_month) ? pay_month : '',
    needs_review_only: row_state === ROW_STATE_NEEDS_REVIEW
  };
};

// The linked month only when it is one the backend listed; otherwise the newest month (the default).
export const pick_initial_month = (months, requested) => {
  const list = Array.isArray(months) ? months : [];
  if (requested && list.some((m) => m && m.pay_month === requested)) return requested;
  return list.length ? list[0].pay_month : '';
};

// a picker value is taken only when it is one of the listed months; the empty "Select a month"
// option is ignored.
export const accept_month_choice = (value, months) =>
  !!value && (Array.isArray(months) ? months : []).some((m) => m && m.pay_month === value);

// ---- view state: lifted into OverviewTab so Back from  restores filters, drafts and page ----

// ---- the list's filters and sort in the URL: teacher, review=1, status, earnings=0|1, sort=key|dir ----

const DEFAULT_SORT = { key: DEFAULT_L1_SORT, dir: DEFAULT_L1_DIR };
// teacher_uuid and row_state=needs_review are the legacy names (old links, the Slack digest): read, never written.
const L1_URL_KEYS = [
  'teacher',
  'teacher_uuid',
  'review',
  'row_state',
  'status',
  'earnings',
  'sort'
];

// Every value is whitelisted; anything unknown falls back to its default.
export const parse_l1_url = (search) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  return {
    teacher_uuid: uuid_param(first_param(qs, ['teacher', 'teacher_uuid'])),
    needs_review_only:
      qs.get('review') === '1' ||
      (qs.get('row_state') || '').trim().toLowerCase() === ROW_STATE_NEEDS_REVIEW,
    teacher_status: enum_param(qs.get('status'), TEACHER_STATUS_VALUES),
    earnings_visible: earnings_filter_param((qs.get('earnings') || '').trim()),
    sort: parse_sort(qs.get('sort'), L1_SORT_KEYS, SORT_DIRS) || DEFAULT_SORT
  };
};

// The search the list writes back: its own keys only, defaults left out, the rest of the URL kept.
export const l1_url_search = (search, applied, sort) =>
  merge_search(search, L1_URL_KEYS, {
    teacher: uuid_param(applied.search),
    review: applied.needs_review_only ? '1' : '',
    status: enum_param(applied.teacher_status, TEACHER_STATUS_VALUES),
    earnings: earnings_filter_param(applied.earnings_visible),
    sort: sort_token(sort, DEFAULT_SORT)
  });

// The first view state, from the URL (the display name is filled in once a row lands).
export const initial_l1_state = (needs_review_only, search = '') => {
  const url = parse_l1_url(search);
  const review = !!needs_review_only || url.needs_review_only;
  return {
    month: '',
    offset: 0,
    applied: {
      search: url.teacher_uuid,
      needs_review_only: review,
      teacher_status: url.teacher_status,
      earnings_visible: url.earnings_visible
    },
    drafts: {
      search: url.teacher_uuid,
      search_display: '',
      review: review ? 'NEEDS_REVIEW' : '',
      status: url.teacher_status,
      earnings: url.earnings_visible
    },
    sort: url.sort
  };
};

export const apply_l1_sort = (state, sort) => ({ ...state, offset: 0, sort });

// Same month → the state as left (page kept); a different month → page 1, filters kept. Returns the SAME object when
// nothing changes so the caller can skip the update.
export const l1_state_for_month = (state, pay_month) =>
  state.month === pay_month ? state : { ...state, month: pay_month, offset: 0 };

// Apply turns the drafts into the query filters and goes back to page 1.
export const apply_l1_drafts = (state) => ({
  ...state,
  offset: 0,
  applied: {
    search: (state.drafts.search || '').trim(),
    needs_review_only: state.drafts.review === 'NEEDS_REVIEW',
    teacher_status: state.drafts.status,
    earnings_visible: earnings_filter_param(state.drafts.earnings)
  }
});

// One filter changed: the drafts take it and apply at once (no Apply button), back to page 1.
export const set_l1_filter = (state, key, value) =>
  apply_l1_drafts({ ...state, drafts: { ...state.drafts, [key]: value } });

// Every filter back to its default; the teacher search too.
export const clear_l1_filters = (state) =>
  apply_l1_drafts({
    ...state,
    drafts: {
      ...state.drafts,
      search: '',
      search_display: '',
      review: '',
      status: '',
      earnings: ''
    }
  });

export const l1_filters_on = (applied) =>
  !!(
    applied.search ||
    applied.needs_review_only ||
    applied.teacher_status ||
    applied.earnings_visible
  );

const status_label = (v) => (TEACHER_STATUS_OPTIONS.find((o) => o.value === v) || {}).label || v;

// The sentence under the filters: what the list shows, in words. Null while there is nothing to say.
export const l1_filter_summary = ({ total, month_label, applied, teacher_name, loading }) => {
  if (loading) return null;
  const noun = total === 1 ? 'teacher' : 'teachers';
  if (!l1_filters_on(applied)) {
    return {
      lead: `all ${total} ${noun}`,
      rest: ` for ${month_label}, any status, any Earnings page.`
    };
  }
  const parts = [];
  if (applied.search) parts.push(teacher_name ? `teacher ${teacher_name}` : 'one teacher');
  if (applied.needs_review_only) parts.push('needs review only');
  parts.push(applied.teacher_status ? status_label(applied.teacher_status) : 'any status');
  if (applied.earnings_visible === '1') parts.push('Earnings page visible');
  else if (applied.earnings_visible === '0') parts.push('Earnings page hidden');
  else parts.push('any Earnings page');
  return { lead: `${total} ${noun}`, rest: ` for ${month_label}: ${parts.join(', ')}.` };
};
